import {productConfig} from '@/lib/productConfig';
import { Link } from "react-router-dom";
import { Flame, Store, Filter, ArrowRight, Star } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import Photo from "@/components/site/Photo";
import ClosingCta from "@/components/site/ClosingCta";
import useSeo from "@/hooks/useSeo";

const hostTypes = [
  { tone: "orange", label: "Event", body: "Started by a person nearby — a run club, coffee catch-up or social meetup.", dot: "bg-orange" },
  { tone: "cobalt", label: "Business Hosted Event", body: "Hosted by a verified local business — networking nights, food deals, workshops and more.", dot: "bg-cobalt" },
];

const filters = [ "Event category", "Date", "Distance"];

const examples = [
  { host: "Business Hosted", tag: "Networking", title: "Local Networking Night", meta: "Friday · 6:00 PM · 350m away", accent: "border-cobalt", badge: "bg-cobalt/10 text-cobalt" },
  { host: "Business Hosted", tag: "Food Deals", title: "Friday Food Special", meta: "5:00–8:00 PM · 20% off · 420m away", accent: "border-cobalt", badge: "bg-cobalt/10 text-cobalt" },
  { host: "People Hosted", tag: "Fitness", title: "Sunset Run Club", meta: "Saturday · 6:30 PM · 500m away", accent: "border-orange", badge: "bg-orange/10 text-orange" },
];

export default function Events() {
  useSeo({
    title: "Events — Orrbbit",
    description: "Discover nearby events on the Orrbbit Radar — Events and local meetups, filtered by category, date and distance.",
    path: "/events",
  });
  return (
    <div data-testid="events-page">
      <section className="bg-lightbg">
        <div className="container-x grid grid-cols-1 items-center gap-12 section-space lg:grid-cols-2">
          <Reveal className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-orange px-3 py-1.5 text-xs font-semibold text-white shadow-soft" data-testid="events-eyebrow">
              <Flame size={13} /> Events
            </span>
            <h1 className="mt-4 font-heading text-4xl font-bold tracking-tight text-navy md:text-5xl">See what's <span className="text-orange">happening</span> around you.</h1>
            <p className="mt-5 max-w-2xl text-lg text-bodygrey">Discover nearby events on your Radar — from people close by — all based on your location, without exposing your exact live location.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/get-started"
                data-testid="events-hero-btn"
                className="inline-flex items-center gap-2 rounded-full bg-orange px-7 py-3.5 font-semibold text-white shadow-soft transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-offset-2"
              >
                Get Started <ArrowRight size={18} />
              </Link>
              <Link
                to="/how-it-works"
                data-testid="events-hero-how-btn"
                className="inline-flex items-center gap-2 rounded-full border-2 border-orange bg-white px-7 py-3.5 font-semibold text-orange transition-colors hover:bg-orange hover:text-white"
              >
                See how it works
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Photo slug="event-watchers" ratio="aspect-[4/3]" priority className="shadow-card" data-testid="events-hero-photo" />
          </Reveal>
        </div>
      </section>

      {/* Two host types */}
      <section className="container-x section-space">
        <Reveal className="max-w-2xl">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">Events that bring people together</h2>
          <p className="mt-3 text-bodygrey">Find a shared interest, see who is hosting and choose how you want to join.</p>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-6">
          {hostTypes.filter(h=>productConfig.businessEnabled||h.tone!=='cobalt').map((h, i) => (
            <Reveal key={h.label} delay={i * 0.06}>
              <div className={`h-full rounded-2xl border bg-white p-7 shadow-soft ${h.tone === "cobalt" ? "border-cobalt/30" : "border-orange/30"}`} data-testid={`event-hosttype-${h.tone}`}>
                <span className="flex items-center gap-2 text-sm font-semibold text-navy">
                  <span className={`h-3 w-3 rounded-full ${h.dot}`} />
                  {h.tone === "cobalt" ? <Store size={16} className="text-cobalt" /> : <Flame size={16} className="text-orange" />}
                  {h.label}
                </span>
                <p className="mt-3 text-bodygrey">{h.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8 inline-flex flex-wrap items-center gap-2 rounded-2xl border border-orange/20 bg-orange/5 p-4 text-sm text-navy">
          <Filter size={16} className="text-orange" /> Filter by:
          {filters.map((f) => (
            <span key={f} className="inline-flex rounded-full border border-border bg-white px-3 py-1 font-medium">{f}</span>
          ))}
        </Reveal>
      </section>

      {/* Examples */}
      <section className="border-t border-border bg-white">
        <div className="container-x section-space">
          <Reveal className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-orange"><Flame size={13} /> Nearby right now</span>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">Happening close by</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-5">
            {examples.filter(e=>productConfig.businessEnabled||e.host!=='Business Hosted').map((e, i) => (
              <Reveal key={e.title} delay={i * 0.06}>
                <div className={`h-full rounded-2xl border-l-4 bg-white p-6 shadow-soft ${e.accent}`}>
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${e.badge}`}>{e.host} · {e.tag}</span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{e.title}</h3>
                  <p className="mt-1 text-sm text-bodygrey">{e.meta}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 flex items-start gap-2 rounded-2xl border border-border bg-lightbg p-5 text-sm text-bodygrey">
            <Star size={16} className="mt-0.5 flex-none text-cobalt" />
            <span>Check Event details and choose public places you are comfortable visiting. You stay in control of whether to connect.</span>
          </Reveal>
          <Reveal className="mt-8 flex flex-wrap gap-3">
            <Link to="/people" className="inline-flex items-center gap-1 font-semibold text-teal hover:underline">Explore People Mode <ArrowRight size={16} /></Link>
            {productConfig.businessEnabled&&<Link to="/business" className="inline-flex items-center gap-1 font-semibold text-cobalt hover:underline">Host events as a business <ArrowRight size={16} /></Link>}
          </Reveal>
        </div>
      </section>

      <ClosingCta photo="event-toast" tone="orange" title="Your next event is already nearby." body="Open the Radar, see what's happening around you, and join in." cta={{ to: "/get-started", label: "Get Started" }} testid="events-closing-cta" />
    </div>
  );
}

